// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import routes from './routes';
import ProtectedRoute from './components/ProtectedRoute';
import { GlobalStateProvider } from './context/GlobalStateContext';
import { PatientDataProvider } from './context/PatientDataContext';
import { PatientSelfDataProvider } from './context/PatientSelfDataContext';
import { ModularPatientSelfDataProvider } from './context/ModularPatientSelfDataContext';
import { ModularInformantSelfDataProvider } from './context/ModularInformantSelfDataContext';
import { SessionTimeout } from './components/SessionTimeout';
import { tabTitleFor } from './rms/utils/rmsTabTitle';
import AccountDisabledOverlay from './components/AccountDisabledOverlay';
import useAccountStatusMonitor from './hooks/useAccountStatusMonitor';
import { ProformaNavigationGuardProvider } from './context/ProformaNavigationGuardContext';
import { analytics } from './firebase';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
// logEvent also feeds the first-party stream via the GA4 bridge;
// page_view is deliberately unbridged so GA4 behaviour is unchanged.
import { logEvent } from './analytics/ga4';
import { sanitizeRoutePath } from './utils/analyticsPaths';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import { VideoStreamProvider } from './context/VideoStreamContext';
// Comment out the consent banner import as we'll disable it for now
// import AnalyticsConsentBanner from './components/AnalyticsConsentBanner';
// Comment out isAnalyticsEnabled as we're not using it while the consent banner is disabled
// import { isAnalyticsEnabled } from './utils/analyticsConsent';

// Monitors the current user's account status in real time and renders
// a blocking overlay if their account has been disabled by an admin.
const AccountStatusGuard = () => {
  const { isAccountDisabled } = useAccountStatusMonitor();
  return <AccountDisabledOverlay isDisabled={isAccountDisabled} />;
};

// Analytics initialization component
const AnalyticsInitializer = () => {
  const location = useLocation();

  // The tab names the service the page belongs to (TDI-831 part 3, R-050):
  // the Digital Hub's pages carry its name, everything else keeps the
  // default, and a page that titles itself (the profile print) is left
  // alone. Before the page-view event below, so analytics carries the title
  // the person saw.
  useEffect(() => {
    const title = tabTitleFor(location.pathname);
    if (title) document.title = title;
  }, [location]);

  // Track page views automatically
  useEffect(() => {
    try {
      // For now, enable analytics by default since we've disabled the consent banner
      // Later, this will be replaced with the user's explicit choice
      const analyticsEnabled = true; // Default to enabled for now
      setAnalyticsCollectionEnabled(analytics, analyticsEnabled);
      
      if (analyticsEnabled) {
        // Log page view event. Only the sanitised route shape is sent: raw
        // paths/URLs can embed patient IDs and other identifiers.
        logEvent(analytics, 'page_view', {
          page_path: sanitizeRoutePath(location.pathname),
          page_title: document.title
        });
      }
    } catch (error) {
      // Silently fail to prevent app disruption
      console.error('Analytics initialization error:', error);
    }
  }, [location]);
  
  return null; // This component doesn't render anything
};

function App() {
  return (
    <GlobalStateProvider>
      <Router>
      {/* <VideoStreamProvider> */}
      <PatientDataProvider>
        <PatientSelfDataProvider>
        <ModularPatientSelfDataProvider>
        <ModularInformantSelfDataProvider>
        <ProformaNavigationGuardProvider>
        <SessionTimeout />
            <AccountStatusGuard />
            {/* Add Analytics Initializer */}
            <AnalyticsInitializer />
        <div className="App">
              <Routes>
                {routes.map(({ path, element, protected: isProtected, allowedRoles }, index) => (
                  <Route
                    key={index}
                    path={path}
                    element={
                      isProtected ? (
                        <ProtectedRoute allowedRoles={allowedRoles}>
                          {element}
                        </ProtectedRoute>
                      ) : (
                        element
                      )
                    }
                  />
                ))}
              </Routes>
            </div>          
            {/* Comment out Analytics Consent Banner until we decide on the consent approach */}
            {/* <AnalyticsConsentBanner privacyPolicyUrl="/privacy-policy" /> */}
            
            {/* Toast Container for notifications */}
            <ToastContainer
              position="bottom-right"
              autoClose={2000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              className="toast-container"
              toastClassName="toast-message"
            />
        </ProformaNavigationGuardProvider>
        </ModularInformantSelfDataProvider>
        </ModularPatientSelfDataProvider>
        </PatientSelfDataProvider>
      </PatientDataProvider>
      {/* </VideoStreamProvider> */}
      </Router>
    </GlobalStateProvider>
  );
}

export default App;